import { requestAuthHeaders } from '@/lib/request-auth-token';
import { resolveApiUrl } from '@/lib/resolve-api-url';
import { useEffect, useState } from 'react';
import { z } from 'zod';

const eventSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('activity'),
    id: z.string(),
    label: z.string(),
    status: z.enum(['active', 'complete', 'error']),
  }),
  z.object({
    type: z.enum(['draft', 'draft.removed']),
    id: z.string(),
    revision: z.number(),
    text: z.string(),
  }),
]);
export type SiteActivity = {
  id: string;
  label: string;
  status: 'active' | 'complete' | 'error';
};
type StreamState = {
  operationId: string | null;
  connected: boolean;
  activities: SiteActivity[];
  drafts: Record<string, { revision: number; text: string }>;
};
const empty = (operationId: string | null): StreamState => ({
  operationId,
  connected: false,
  activities: [],
  drafts: {},
});

export function useSiteStream(
  path: string,
  operationId: string | null,
  enabled: boolean
) {
  const [state, setState] = useState<StreamState>(() => empty(null));
  useEffect(() => {
    if (!enabled || !operationId) return;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let cursor = '';
    setState(empty(operationId));
    async function connect() {
      try {
        const response = await fetch(
          resolveApiUrl(
            `${path}/events${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`
          ),
          {
            headers: { Accept: 'text/event-stream', ...requestAuthHeaders() },
            credentials: 'include',
            signal: controller.signal,
          }
        );
        if (controller.signal.aborted) return;
        if (response.status === 410) cursor = '';
        if (response.ok && response.status !== 204 && response.body) {
          setState((current) => ({ ...current, connected: true }));
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          try {
            while (!controller.signal.aborted) {
              const { done, value } = await reader.read();
              if (done || controller.signal.aborted) break;
              buffer += decoder.decode(value, { stream: true });
              let end = buffer.indexOf('\n\n');
              while (end >= 0) {
                const frame = buffer.slice(0, end);
                buffer = buffer.slice(end + 2);
                const line = frame
                  .split('\n')
                  .find((value) => value.startsWith('data:'));
                const eventId = frame
                  .split('\n')
                  .find((value) => value.startsWith('id:'));
                // Transport cursors acknowledge complete frames, including ones
                // whose payload cannot be rendered. Don't replay poison frames.
                if (eventId) cursor = eventId.slice(3).trim();
                if (line) {
                  let payload: unknown;
                  try {
                    payload = JSON.parse(line.slice(5));
                  } catch {
                    // Keep processing later frames; saved-state polling is authoritative.
                  }
                  const event = eventSchema.safeParse(payload);
                  if (event.success)
                    setState((current) => {
                      if (current.operationId !== operationId) return current;
                      const value = event.data;
                      if (value.type === 'activity') {
                        const activities = current.activities.filter(
                          (item) => item.id !== value.id
                        );
                        return {
                          ...current,
                          activities: [...activities, value].slice(-16),
                        };
                      }
                      const drafts = { ...current.drafts };
                      if (value.type === 'draft.removed')
                        delete drafts[value.id];
                      else if (
                        !drafts[value.id] ||
                        value.revision >= drafts[value.id].revision
                      )
                        drafts[value.id] = {
                          revision: value.revision,
                          text: value.text,
                        };
                      return { ...current, drafts };
                    });
                }
                end = buffer.indexOf('\n\n');
              }
              if (buffer.length > 256000)
                throw new Error('Live update is too large');
            }
          } finally {
            await reader.cancel().catch(() => undefined);
            reader.releaseLock();
          }
        }
      } catch {
        /* Saved-state polling continues while the event feed reconnects. */
      }
      if (!controller.signal.aborted) {
        setState((current) => ({ ...current, connected: false }));
        timer = setTimeout(() => void connect(), 3000);
      }
    }
    void connect();
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [path, operationId, enabled]);
  return enabled && state.operationId === operationId
    ? state
    : empty(operationId);
}
